import { ApiError, unwrap } from "@brad-tipper/app-frontend";

import { client } from "./client";
import type { TableName, Tables } from "./tables";

/**
 * A small table-style query layer over the REST API, so the workspace hooks keep their
 * `from(table).select().eq()` shape. Each table is a REST resource (`user_rates` ->
 * `/v1/user-rates`). Filters are sent as query parameters so the server narrows the
 * list, and every filter is applied again here, so a filter the server does not know
 * is still honoured. Sorting and limiting happen here too.
 */

export type DbError = Error & { code: string; status: number };

type Row = Record<string, unknown>;
type Result<T> = { data: T; error: DbError | null; count?: number };
type Filter = {
  column: string;
  op: "eq" | "neq" | "gte" | "lte" | "gt" | "lt" | "in" | "is";
  value: unknown;
};
type Order = { column: string; ascending: boolean };

/** Related rows a select can embed: how to fetch them for a set of parent rows. */
const EMBEDS: Record<
  string,
  Record<
    string,
    (rows: Row[], filters: Filter[], columns: string) => Promise<void>
  >
> = {
  expenses: {
    async expense_categories(rows, filters, columns) {
      const workspaceId = filters.find(
        (f) => f.column === "workspace_id",
      )?.value;
      if (!workspaceId) return;
      const categories = await list("expense_categories", [
        { column: "workspace_id", op: "eq", value: workspaceId },
      ]);
      const names = new Map(categories.map((c) => [c.id as string, c]));
      const wanted = columns.split(",").map((c) => c.trim());
      for (const row of rows) {
        const category = row.category_id
          ? names.get(row.category_id as string)
          : undefined;
        row.expense_categories = category
          ? Object.fromEntries(wanted.map((c) => [c, category[c]]))
          : null;
      }
    },
  },
  quotes: {
    async quote_items(rows) {
      for (const row of rows)
        row.quote_items = (row.items as Row[] | undefined) ?? [];
    },
  },
  time_entries: {
    async time_entry_tag_links(rows) {
      for (const row of rows) {
        row.time_entry_tag_links = ((row.tags as Row[] | undefined) ?? []).map(
          (tag) => ({
            time_entry_tags: { id: tag.id, name: tag.name },
          }),
        );
      }
    },
  },
  fixed_assets: {
    async asset_depreciation_entries(rows, filters) {
      const workspaceId = filters.find(
        (f) => f.column === "workspace_id",
      )?.value;
      if (!workspaceId) return;
      const entries = await list("asset_depreciation_entries", [
        { column: "workspace_id", op: "eq", value: workspaceId },
      ]);
      for (const row of rows)
        row.asset_depreciation_entries = entries.filter(
          (e) => e.asset_id === row.id,
        );
    },
  },
};

function toError(cause: unknown): DbError {
  const api = cause instanceof ApiError ? cause : null;
  const error = new Error(
    api
      ? api.message
      : cause instanceof Error
        ? cause.message
        : "Request failed.",
  ) as DbError;
  error.code = api?.code ?? "error";
  error.status = api?.status ?? 0;
  return error;
}

const pathFor = (table: string) => `/v1/${table.replaceAll("_", "-")}`;

function compare(a: unknown, b: unknown) {
  if (a === b) return 0;
  if (a === null || a === undefined) return 1; // nulls last when ascending
  if (b === null || b === undefined) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean")
    return Number(a) - Number(b);
  return String(a) < String(b) ? -1 : 1;
}

function matches(row: Row, filter: Filter) {
  const value = row[filter.column];
  const wanted = filter.value;
  switch (filter.op) {
    case "eq":
      return (
        value === wanted ||
        (value != null && wanted != null && String(value) === String(wanted))
      );
    case "neq":
      return !(
        value === wanted ||
        (value != null && wanted != null && String(value) === String(wanted))
      );
    case "is":
      return wanted === null
        ? value === null || value === undefined
        : value === wanted;
    case "in":
      return (wanted as unknown[]).some(
        (candidate) => String(candidate) === String(value),
      );
    case "gte":
      return value != null && compare(value, wanted) >= 0;
    case "lte":
      return value != null && compare(value, wanted) <= 0;
    case "gt":
      return value != null && compare(value, wanted) > 0;
    case "lt":
      return value != null && compare(value, wanted) < 0;
  }
}

function queryFor(filters: Filter[]) {
  const query: Record<string, string> = {};
  for (const filter of filters) {
    if (filter.column === "id") continue;
    const value = filter.value;
    if (filter.op === "eq" && value !== undefined)
      query[filter.column] = value === null ? "null" : String(value);
    else if (filter.op === "is" && value === null)
      query[filter.column] = "null";
    else if (filter.op === "gte") query[`${filter.column}_gte`] = String(value);
    else if (filter.op === "lte") query[`${filter.column}_lte`] = String(value);
    else if (filter.op === "in")
      query[`${filter.column}_in`] = (value as unknown[]).join(",");
  }
  return query;
}

async function list(table: string, filters: Filter[]): Promise<Row[]> {
  const idFilter = filters.find((f) => f.column === "id" && f.op === "eq");
  if (idFilter) {
    try {
      const row = (await unwrap(
        client.get({
          url: `${pathFor(table)}/${String(idFilter.value)}`,
        }) as never,
      )) as Row;
      return [row].filter((candidate) =>
        filters.every((f) => matches(candidate, f)),
      );
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 404) return [];
      throw cause;
    }
  }
  const data = (await unwrap(
    client.get({ url: pathFor(table), query: queryFor(filters) }) as never,
  )) as { items: Row[] };
  return data.items.filter((row) => filters.every((f) => matches(row, f)));
}

class Query<R = Row, T = R[]> implements PromiseLike<Result<T>> {
  private filters: Filter[] = [];
  private orders: Order[] = [];
  private max: number | null = null;
  private mode: "many" | "single" | "maybe" = "many";
  private action: "select" | "insert" | "update" | "delete" | "upsert" =
    "select";
  private columns = "*";
  private payload: Row | Row[] | null = null;
  private returning = false;
  private head = false;
  private counted = false;
  private onConflict: string[] = [];

  constructor(private readonly table: string) {}

  select(columns = "*", options?: { count?: "exact"; head?: boolean }) {
    if (this.action === "select") this.columns = columns;
    else this.returning = true;
    this.head = Boolean(options?.head);
    this.counted = Boolean(options?.count);
    return this;
  }
  insert(values: Partial<R> | Partial<R>[]) {
    this.action = "insert";
    this.payload = values as Row | Row[];
    return this;
  }
  update(values: Partial<R>) {
    this.action = "update";
    this.payload = values as Row;
    return this;
  }
  upsert(values: Partial<R> | Partial<R>[], options?: { onConflict?: string }) {
    this.action = "upsert";
    this.payload = values as Row | Row[];
    this.onConflict =
      options?.onConflict?.split(",").map((c) => c.trim()) ?? [];
    return this;
  }
  delete() {
    this.action = "delete";
    return this;
  }
  eq(column: string, value: unknown) {
    this.filters.push({ column, op: "eq", value });
    return this;
  }
  neq(column: string, value: unknown) {
    this.filters.push({ column, op: "neq", value });
    return this;
  }
  gte(column: string, value: unknown) {
    this.filters.push({ column, op: "gte", value });
    return this;
  }
  lte(column: string, value: unknown) {
    this.filters.push({ column, op: "lte", value });
    return this;
  }
  gt(column: string, value: unknown) {
    this.filters.push({ column, op: "gt", value });
    return this;
  }
  lt(column: string, value: unknown) {
    this.filters.push({ column, op: "lt", value });
    return this;
  }
  in(column: string, value: unknown[]) {
    this.filters.push({ column, op: "in", value });
    return this;
  }
  is(column: string, value: unknown) {
    this.filters.push({ column, op: "is", value });
    return this;
  }
  match(values: Row) {
    for (const [column, value] of Object.entries(values))
      this.eq(column, value);
    return this;
  }
  order(column: string, options?: { ascending?: boolean }) {
    this.orders.push({ column, ascending: options?.ascending ?? true });
    return this;
  }
  limit(count: number) {
    this.max = count;
    return this;
  }
  single() {
    this.mode = "single";
    return this as unknown as Query<R, R>;
  }
  maybeSingle() {
    this.mode = "maybe";
    return this as unknown as Query<R, R | null>;
  }

  then<R1 = Result<T>, R2 = never>(
    onfulfilled?: ((value: Result<T>) => R1 | PromiseLike<R1>) | null,
    onrejected?: ((reason: unknown) => R2 | PromiseLike<R2>) | null,
  ): PromiseLike<R1 | R2> {
    return this.run().then(onfulfilled, onrejected);
  }

  private async run(): Promise<Result<T>> {
    try {
      const rows = await this.execute();
      return this.shape(rows);
    } catch (cause) {
      return {
        data: (this.mode === "many" ? [] : null) as T,
        error: toError(cause),
      };
    }
  }

  private shape(rows: Row[]): Result<T> {
    if (this.mode === "many") {
      return {
        data: (this.head ? null : rows) as T,
        error: null,
        ...(this.counted ? { count: rows.length } : {}),
      };
    }
    if (rows.length > 1 && this.mode === "single") {
      return {
        data: null as T,
        error: Object.assign(new Error("Multiple rows returned."), {
          code: "PGRST116",
          status: 406,
        }),
      };
    }
    if (rows.length === 0 && this.mode === "single") {
      return {
        data: null as T,
        error: Object.assign(new Error("No rows found."), {
          code: "PGRST116",
          status: 406,
        }),
      };
    }
    return { data: (rows[0] ?? null) as T, error: null };
  }

  private async execute(): Promise<Row[]> {
    switch (this.action) {
      case "select":
        return this.read();
      case "insert":
        return this.create(this.payload as Row | Row[]);
      case "upsert":
        return this.upsertRows(this.payload as Row | Row[]);
      case "update": {
        const targets = await this.targets();
        const out: Row[] = [];
        for (const target of targets) {
          out.push(
            (await unwrap(
              client.patch({
                url: `${pathFor(this.table)}/${target.id as string}`,
                body: this.payload,
                headers: { "Content-Type": "application/json" },
              }) as never,
            )) as Row,
          );
        }
        return this.returning ? out : [];
      }
      case "delete": {
        for (const target of await this.targets()) {
          await unwrap(
            client.delete({
              url: `${pathFor(this.table)}/${target.id as string}`,
            }) as never,
          );
        }
        return [];
      }
    }
  }

  /** The rows a write applies to: the filtered list (one request when it filters by id). */
  private async targets(): Promise<Row[]> {
    const idFilter = this.filters.find(
      (f) => f.column === "id" && f.op === "eq",
    );
    // A workspace guard next to an id needs no lookup: the server checks access to the row itself.
    if (
      idFilter &&
      this.filters.every((f) => f === idFilter || f.column === "workspace_id")
    ) {
      return [{ id: idFilter.value }];
    }
    return list(this.table, this.filters);
  }

  private async create(values: Row | Row[]): Promise<Row[]> {
    const out: Row[] = [];
    for (const value of Array.isArray(values) ? values : [values]) {
      out.push(
        (await unwrap(
          client.post({
            url: pathFor(this.table),
            body: value,
            headers: { "Content-Type": "application/json" },
          }) as never,
        )) as Row,
      );
    }
    return this.returning ? out : [];
  }

  private async upsertRows(values: Row | Row[]): Promise<Row[]> {
    const out: Row[] = [];
    for (const value of Array.isArray(values) ? values : [values]) {
      const keys = this.onConflict.length ? this.onConflict : ["id"];
      const probe: Filter[] = keys
        .filter((k) => value[k] !== undefined)
        .map((k) => ({ column: k, op: "eq", value: value[k] }));
      const existing =
        probe.length === keys.length && this.table !== "device_push_tokens"
          ? await list(this.table, probe)
          : [];
      if (existing[0]) {
        out.push(
          (await unwrap(
            client.patch({
              url: `${pathFor(this.table)}/${existing[0].id as string}`,
              body: value,
              headers: { "Content-Type": "application/json" },
            }) as never,
          )) as Row,
        );
      } else {
        out.push(
          (await unwrap(
            client.post({
              url: pathFor(this.table),
              body: value,
              headers: { "Content-Type": "application/json" },
            }) as never,
          )) as Row,
        );
      }
    }
    return this.returning ? out : [];
  }

  private async read(): Promise<Row[]> {
    let rows = await list(this.table, this.filters);
    for (const spec of this.embeds()) {
      await EMBEDS[this.table]?.[spec.name]?.(rows, this.filters, spec.columns);
    }
    for (const order of [...this.orders].reverse()) {
      rows = [...rows].sort((a, b) => {
        const delta = compare(a[order.column], b[order.column]);
        // Postgres: nulls last ascending, first descending.
        return order.ascending ? delta : -delta;
      });
    }
    if (this.max !== null) rows = rows.slice(0, this.max);
    return rows;
  }

  private embeds() {
    const found: { name: string; columns: string }[] = [];
    const pattern = /([a-z_]+)\(((?:[^()]|\([^()]*\))*)\)/g;
    for (const match of this.columns.matchAll(pattern))
      found.push({ name: match[1]!, columns: match[2]! });
    return found;
  }
}

export const db = {
  from<N extends TableName>(table: N) {
    return new Query<Tables<N>>(table);
  },
};
